/**
 * usePathfinding - A* algorithm for Pac-Man navigation
 * Used only by Pac-Man to chase ghosts, not by ghosts
 */

import { useCallback } from 'react';
import type { GridPosition, Maze } from '../types/pacman';

interface PathNode {
  position: GridPosition;
  g: number;
  h: number;
  f: number;
  parent: PathNode | null;
}

export function usePathfinding() {
  const findPath = useCallback(
    (start: GridPosition, end: GridPosition, maze: Maze): GridPosition[] => {
      const openSet: PathNode[] = [];
      const closedSet = new Set<string>();

      const posKey = (p: GridPosition) => `${p.x},${p.y}`;
      const heuristic = (a: GridPosition) =>
        Math.abs(a.x - end.x) + Math.abs(a.y - end.y);

      openSet.push({
        position: start,
        g: 0,
        h: heuristic(start),
        f: heuristic(start),
        parent: null,
      });

      while (openSet.length > 0) {
        // Get node with lowest f
        openSet.sort((a, b) => a.f - b.f);
        const current = openSet.shift()!;

        // Found the goal
        if (current.position.x === end.x && current.position.y === end.y) {
          return reconstructPath(current);
        }

        closedSet.add(posKey(current.position));

        // Check neighbors
        const neighbors = [
          { x: current.position.x - 1, y: current.position.y },
          { x: current.position.x + 1, y: current.position.y },
          { x: current.position.x, y: current.position.y - 1 },
          { x: current.position.x, y: current.position.y + 1 },
        ];

        for (const pos of neighbors) {
          // Skip out of bounds
          if (pos.x < 0 || pos.x >= maze.width || pos.y < 0 || pos.y >= maze.height) {
            continue;
          }
          // Skip walls
          if (maze.cells[pos.y]?.[pos.x]?.type === 'WALL') {
            continue;
          }
          // Skip already visited
          if (closedSet.has(posKey(pos))) {
            continue;
          }

          const g = current.g + 1;
          const existing = openSet.find(
            n => n.position.x === pos.x && n.position.y === pos.y
          );

          if (existing) {
            if (g < existing.g) {
              existing.g = g;
              existing.f = g + existing.h;
              existing.parent = current;
            }
          } else {
            const h = heuristic(pos);
            openSet.push({
              position: pos,
              g,
              h,
              f: g + h,
              parent: current,
            });
          }
        }
      }

      // No path found
      return [start];
    },
    []
  );

  return { findPath };
}

function reconstructPath(node: PathNode): GridPosition[] {
  const path: GridPosition[] = [];
  let current: PathNode | null = node;

  while (current) {
    path.unshift(current.position);
    current = current.parent;
  }

  return path;
}
